/**
 * BrainProcessingCard
 *
 * Replaces the basic bouncing-dots loading indicator during Brain.Learning
 * instantiation calls with a rich, phased processing animation and a
 * quantitative results summary once the kit is ready.
 *
 * Two visual states:
 *   1. **Processing** – animated phases with step dots and progress bar.
 *   2. **Complete**   – quantitative kit summary with auto-continue CTA.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import { useLocale } from '@tonnie/locale';
import { Card, CardContent, Button, cn } from '@tonnie/ui';
import {
  Sparkles,
  CheckCircle2,
  FileText,
  Star,
  Users,
  Scale,
  Plane,
  Home,
  ArrowRight,
  type LucideIcon,
} from '@tonnie/assets';
import { motion, AnimatePresence } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface KitSummary {
  tracks: Array<{ track_code: string; order_index: number }>;
  tasks: Array<{
    track: string;
    requires_document: boolean;
    vault_linked: boolean;
    household_member_id: string | null;
  }>;
  complexity: { score: number; level: 'simple' | 'standard' | 'complex'; factors: string[] };
  metadata: {
    origin: string;
    destination: string;
    total_tasks: number;
    total_documents_required: number;
    vault_linked_count: number;
    household_member_count: number;
    generated_at: string;
  };
}

export interface BrainProcessingCardProps {
  status: 'processing' | 'complete' | 'error';
  kit: KitSummary | null;
  startedAt: number;
  /**
   * The API's error code when `status` is `'error'`. A destination with no kit
   * (TELEPORTAL_004) is a refusal retrying cannot change, so it gets its own
   * words and no Retry; anything else is the kit engine not answering.
   */
  errorCode?: string | null;
  onContinue: () => void;
  onRetry?: () => void;
}

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

const PHASE_INTERVAL_MS = 3500;
const AUTO_CONTINUE_MS = 4000;

const TRACK_ICONS: Record<string, LucideIcon> = { legal: Scale, travel: Plane, setup: Home };
const TRACK_COLORS: Record<string, string> = {
  legal: 'text-tone-blue',
  travel: 'text-tone-amber',
  setup: 'text-tone-green',
};
const TRACK_BG: Record<string, string> = {
  legal: 'bg-tone-blue-subtle',
  travel: 'bg-tone-amber-subtle',
  setup: 'bg-tone-green-subtle',
};

const COMPLEXITY_BADGE: Record<string, { variant: string }> = {
  simple: { variant: 'bg-tone-green-subtle text-tone-green' },
  standard: { variant: 'bg-tone-amber-subtle text-tone-amber' },
  complex: { variant: 'bg-tone-red-subtle text-tone-red' },
};

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function BrainProcessingCard({
  status,
  kit,
  startedAt,
  errorCode = null,
  onContinue,
  onRetry,
}: BrainProcessingCardProps) {
  const { t } = useLocale();
  const [activePhase, setActivePhase] = useState(0);
  const [autoContinueProgress, setAutoContinueProgress] = useState(0);
  const mountedRef = useRef(true);
  const continueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const phases = [
    t('teleportal.brain.phase1'),
    t('teleportal.brain.phase2'),
    t('teleportal.brain.phase3'),
    t('teleportal.brain.phase4'),
    t('teleportal.brain.phase5'),
  ];

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (status !== 'processing') return;
    const timer = setInterval(() => {
      if (!mountedRef.current) return;
      setActivePhase((prev) => (prev < phases.length - 1 ? prev + 1 : prev));
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status, phases.length]);

  useEffect(() => {
    if (status !== 'complete') return;

    const step = 50;
    progressRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      setAutoContinueProgress((prev) => {
        const next = prev + (step / AUTO_CONTINUE_MS) * 100;
        return Math.min(next, 100);
      });
    }, step);

    continueTimerRef.current = setTimeout(() => {
      if (mountedRef.current) onContinue();
    }, AUTO_CONTINUE_MS);

    return () => {
      if (continueTimerRef.current) clearTimeout(continueTimerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [status, onContinue]);

  const handleContinueNow = useCallback(() => {
    if (continueTimerRef.current) clearTimeout(continueTimerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    onContinue();
  }, [onContinue]);

  const elapsed = Math.round((Date.now() - startedAt) / 1000);

  if (status === 'error') {
    const destinationNotSupported = errorCode === DOMAIN_ERROR_CODES.TELEPORTAL_004;
    return (
      <Card className="border-destructive/30 bg-destructive/5" data-testid="guide-brain-processing">
        <CardContent className="py-6 text-center space-y-3">
          <div className="w-10 h-10 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-destructive-accessible" />
          </div>
          <p className="text-sm font-medium text-destructive-accessible">
            {destinationNotSupported
              ? t('teleportal.brain.destinationNotSupported')
              : t('teleportal.brain.error')}
          </p>
          {onRetry && !destinationNotSupported && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (status === 'complete' && kit) {
    return (
      <KitResultsSummary kit={kit} progress={autoContinueProgress} onContinue={handleContinueNow} />
    );
  }

  return (
    <Card
      className="border-primary/20 bg-primary/5 overflow-hidden"
      data-testid="guide-brain-processing"
    >
      <CardContent className="pt-6 pb-5">
        <div className="flex flex-col items-center text-center space-y-5">
          {/* Animated icon */}
          <div className="relative">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center"
            >
              <Sparkles className="w-7 h-7 text-primary-accessible" />
            </motion.div>
            <motion.div
              className="absolute -inset-2 rounded-3xl border-2 border-primary/20"
              animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>

          {/* Title */}
          <div>
            <h4 className="text-base font-semibold">{t('teleportal.brain.title')}</h4>
            <p className="text-xs text-muted-foreground mt-1">{t('teleportal.brain.subtitle')}</p>
          </div>

          {/* Phase text with animation */}
          <div className="h-6 flex items-center" data-testid="guide-brain-processing-phase">
            <AnimatePresence mode="wait">
              <motion.p
                key={activePhase}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="text-sm text-muted-foreground"
              >
                {phases[activePhase]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Step dots */}
          <div className="flex items-center gap-2">
            {phases.map((_, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className={cn(
                    'w-2.5 h-2.5 rounded-full transition-all duration-500',
                    idx < activePhase
                      ? 'bg-primary scale-100'
                      : idx === activePhase
                        ? 'bg-primary/70 scale-125'
                        : 'bg-muted-foreground/20 scale-100'
                  )}
                />
                {idx < phases.length - 1 && (
                  <div
                    className={cn(
                      'w-6 h-0.5 rounded-full transition-colors duration-500',
                      idx < activePhase ? 'bg-primary/40' : 'bg-muted-foreground/10'
                    )}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Elapsed time */}
          <p className="text-2xs text-muted-foreground tabular-nums">
            {elapsed > 0 && `${elapsed}s`}
          </p>
        </div>
      </CardContent>

      {/* Progress bar at bottom */}
      <div className="h-1 bg-primary/10">
        <motion.div
          className="h-full bg-primary/30"
          animate={{ width: ['0%', '70%', '40%', '85%', '60%', '95%'] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// KIT RESULTS SUMMARY (shown after processing completes)
// ═══════════════════════════════════════════════════════════════════════

function KitResultsSummary({
  kit,
  progress,
  onContinue,
}: {
  kit: KitSummary;
  progress: number;
  onContinue: () => void;
}) {
  const { t } = useLocale();

  const tasksByTrack = kit.tracks.map((track) => {
    const count = kit.tasks.filter((task) => task.track === track.track_code).length;
    const Icon = TRACK_ICONS[track.track_code] ?? Home;
    return { code: track.track_code, count, Icon };
  });

  const complexityInfo = COMPLEXITY_BADGE[kit.complexity.level] ?? COMPLEXITY_BADGE.standard;
  const docsNeeded = kit.tasks.filter(
    (task) => task.requires_document && !task.vault_linked
  ).length;
  const docsLinked = kit.tasks.filter((task) => task.vault_linked).length;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        className="border-primary/20 bg-primary/5 overflow-hidden"
        data-testid="guide-brain-processing-kit-results"
      >
        <CardContent className="pt-5 pb-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-tone-green-subtle flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-tone-green" />
              </div>
              <div>
                <h4 className="text-base font-semibold">{t('teleportal.brain.ready')}</h4>
                <p className="text-xs text-muted-foreground">
                  {kit.metadata.origin} → {kit.metadata.destination}
                </p>
              </div>
            </div>
            <span
              className={cn('text-xs font-medium px-2.5 py-1 rounded-full', complexityInfo.variant)}
            >
              {t(`teleportal.brain.complexity.${kit.complexity.level}`)}
            </span>
          </div>

          {/* Quantitative grid */}
          <div className="grid grid-cols-4 gap-3">
            <StatCell
              icon={CheckCircle2}
              value={kit.metadata.total_tasks}
              label={t('teleportal.brain.stat.tasks')}
            />
            <StatCell
              icon={FileText}
              value={docsNeeded}
              label={t('teleportal.brain.stat.docsNeeded')}
            />
            <StatCell icon={Star} value={docsLinked} label={t('teleportal.brain.stat.fromVault')} />
            <StatCell
              icon={Users}
              value={kit.metadata.household_member_count}
              label={t('teleportal.brain.stat.members')}
            />
          </div>

          {/* Track breakdown */}
          <div className="flex gap-2">
            {tasksByTrack.map(({ code, count, Icon }) => (
              <div
                key={code}
                className={cn(
                  'flex-1 flex items-center gap-2 px-3 py-2 rounded-lg',
                  TRACK_BG[code] ?? 'bg-muted/50'
                )}
              >
                <Icon className={cn('w-4 h-4', TRACK_COLORS[code] ?? 'text-muted-foreground')} />
                <div>
                  <p className="text-sm font-bold tabular-nums">{count}</p>
                  <p className="text-2xs text-muted-foreground capitalize">{code}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={onContinue}
            className="w-full gap-2"
            size="lg"
            data-testid="guide-brain-processing-continue"
          >
            {t('teleportal.brain.reviewKit')}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>

        {/* Auto-continue progress bar */}
        <div className="h-1 bg-primary/10">
          <div
            className="h-full bg-primary/40 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </Card>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STAT CELL
// ═══════════════════════════════════════════════════════════════════════

function StatCell({
  icon: Icon,
  value,
  label,
}: {
  icon: LucideIcon;
  value: number;
  label: string;
}) {
  return (
    <div className="text-center p-2.5 rounded-lg bg-background/60">
      <Icon className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <p className="text-2xs text-muted-foreground">{label}</p>
    </div>
  );
}
